import { Directive, Inject, Input, OnInit, Optional, TemplateRef, ViewContainerRef } from '@angular/core'
import { UserGroupPermissionId } from '@ftr/contracts/api/user-group'
import { Uuid } from '@ftr/contracts/type/shared'
import { Store } from '@ngxs/store'
import { distinctUntilChanged, map } from 'rxjs'
import { UserState } from '../store'
import { PERMISSION_OVERRIDES, PermissionOverrides } from './permission-overrides.service'

/**
 * This directive shows or hides an element based on whether the user has a certain
 * permissions in the court system. If courtSystemId is not specified, it will check the
 * user's permissions in the current court system.
 *
 * You can pass in a single permission or you can pass in multiple. If you pass in multiple
 * permission it will show if the user has any of the passed in permissions (acting as an OR)
 *
 * If you wish to show an element if the user has multiple permissions you can nest <ng-container>.
 *
 * Example usage:
 * 1. Show div if the user has an AdministerUserGroups permissions in their current court system:
 * <div *ifHasPermission="permissions.AdministerUserGroups"></div>
 *
 * 2. Show div if the user has an AdministerUserGroups permissions in the court system with id === 'abc'
 * <div *ifHasPermission="permissions.AdministerUserGroups;courtSystemId:'abc'"></div>
 *
 * 3. Show div if the user has either ProcessTranscriptOrders or ProcessAudioOrders
 * feature enabled:
 * <div
 *  *ifHasPermission="[
 *    permissions.ProcessTranscriptOrders,
 *    permissions.ProcessAudioOrders,
 *  ]"
 * ></div>
 *
 * 4. Show div if the user has both ProcessTranscriptOrders and ProcessAudioOrders
 * <ng-container *ifHasPermission="permissions.ProcessTranscriptOrders">
 *   <div *ifHasPermission="permissions.ProcessAudioOrders"></div>
 * </ng-container>
 */
@Directive({
  selector: '[ifHasPermission]',
  standalone: true,
})
export class IfHasPermissionDirective implements OnInit {
  private permissions: UserGroupPermissionId[]
  private courtSystemId: Uuid | undefined
  private falseViewRef: TemplateRef<any> | null

  constructor(
    private trueViewRef: TemplateRef<any>,
    private viewContainer: ViewContainerRef,
    private store: Store,
    // Storybook exclusive
    @Optional() @Inject(PERMISSION_OVERRIDES) public overrides: PermissionOverrides | undefined,
  ) {}

  ngOnInit(): void {
    if (this.overrides) {
      const availableViaOverrides = new Set(this.overrides.permissions)
      const hasPermission = this.permissions.some(permission => availableViaOverrides.has(permission))
      this.alterView(hasPermission)
    } else {
      this.store
        .select(UserState.hasAnyPermissionInCourtSystem)
        .pipe(
          map(fn => fn(this.permissions, this.courtSystemId)),
          distinctUntilChanged(),
        )
        .subscribe(hasPermission => this.alterView(hasPermission))
    }
  }

  private alterView(hasPermission: boolean): void {
    if (hasPermission) {
      this.viewContainer.createEmbeddedView(this.trueViewRef)
    } else if (!hasPermission && this.falseViewRef) {
      this.viewContainer.createEmbeddedView(this.falseViewRef)
    } else {
      this.viewContainer.clear()
    }
  }

  @Input()
  set ifHasPermission(permissionId: UserGroupPermissionId | UserGroupPermissionId[]) {
    this.permissions = !Array.isArray(permissionId) ? [permissionId] : permissionId
  }

  @Input()
  set ifHasPermissionCourtSystemId(value: Uuid | undefined) {
    this.courtSystemId = value
  }

  @Input()
  set ifHasPermissionElse(templateRef: TemplateRef<any> | null) {
    assertTemplate('ifHasPermissionElse', templateRef)
    this.falseViewRef = templateRef
  }
}

function assertTemplate(property: string, templateRef: TemplateRef<any> | null): void {
  const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView)
  if (!isTemplateRefOrNull) {
    throw new Error(`${property} must be a TemplateRef, but received '${templateRef}'.`)
  }
}
